// File: frontend/src/pages/timetable/MyTimetable.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/axios';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MyTimetable = () => {
  const { user } = useAuth();
  const [selectedDay, setSelectedDay] = useState(() => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const current = dayNames[new Date().getDay()];
    return DAYS.includes(current) ? current : 'Monday';
  });

  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const userClass = user?.class_id || user?.class || 'Class 10';

  useEffect(() => {
    const fetchUserTimetable = async () => {
      try {
        setLoading(true);
        setError('');
        let endpoint = `/api/v1/timetable/class/${encodeURIComponent(userClass)}`;
        
        if (user?.role === 'teacher') {
          endpoint = `/api/v1/timetable/teacher/${user?.id || user?.username}`;
        }

        const res = await api.get(endpoint);
        if (res.data?.success) {
          setTimetable(res.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching timetable:', err);
        setError(err.response?.data?.message || 'Failed to load your schedule.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserTimetable();
  }, [userClass, user]);

  const filteredEntries = timetable.filter(
    (item) => item.day_of_week === selectedDay
  );

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header Banner */}
        <div className="bg-gradient-to-r from-blue-900/40 via-slate-800 to-indigo-900/40 p-6 rounded-2xl border border-slate-700/60 shadow-xl backdrop-blur-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">My Class Schedule</h1>
                <p className="text-xs sm:text-sm text-slate-400">
                  {user?.role === 'teacher' ? `Schedule for ${user?.name || 'Teacher'}` : `Class Schedule for ${userClass}`}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 transition flex items-center space-x-2 print:hidden"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Print Timetable</span>
          </button>
        </div>

        {/* Day Selector */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-none border-b border-slate-800">
          {DAYS.map((day) => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition whitespace-nowrap ${
                selectedDay === day
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : 'bg-slate-800/60 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
              }`}
            >
              {day}
            </button>
          ))}
        </div>

        {/* Schedule List / Grid */}
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <div className="inline-block animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mb-3"></div>
            <p>Loading schedule...</p>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-sm">
            {error}
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="bg-slate-800/40 p-12 rounded-2xl border border-dashed border-slate-700 text-center">
            <p className="text-slate-300 font-semibold mb-1">No classes scheduled for {selectedDay}</p>
            <p className="text-xs text-slate-500">Enjoy your free time!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                className="bg-slate-800/70 p-4 sm:p-5 rounded-2xl border border-slate-700/60 hover:border-blue-500/40 transition flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-md"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/30 text-blue-400 font-extrabold rounded-xl flex items-center justify-center text-lg shadow-inner">
                    #{entry.period_number}
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-white">{entry.subject}</h3>
                    <div className="flex items-center space-x-4 text-xs text-slate-400 mt-0.5">
                      <span>Teacher: <strong className="text-slate-200">{entry.teacher_name || 'N/A'}</strong></span>
                      {entry.room_number && (
                        <span>• Room: <strong className="text-slate-200">{entry.room_number}</strong></span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900/80 px-4 py-2 rounded-xl border border-slate-700/80 self-start sm:self-center text-xs font-mono text-blue-300">
                  {entry.start_time} - {entry.end_time}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default MyTimetable;
